// Browser console logger with remote logging capability
// Captures all console logs and optionally sends to server

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  data?: any;
  userAgent: string;
  url: string;
}

class Logger {
  private logs: LogEntry[] = [];
  private maxLogs = 100;
  private remoteLogging = false;
  private remoteUrl = '/api/logs'; // Optional remote endpoint

  constructor() {
    this.interceptConsole();
    this.setupErrorHandler();
  }

  private interceptConsole() {
    const originalConsole = {
      log: console.log,
      info: console.info,
      warn: console.warn,
      error: console.error,
      debug: console.debug,
    };

    const createInterceptor = (level: LogLevel, original: typeof console.log) => {
      return (...args: any[]) => {
        original.apply(console, args);
        this.addLog(level, args);
      };
    };

    console.log = createInterceptor('log', originalConsole.log);
    console.info = createInterceptor('info', originalConsole.info);
    console.warn = createInterceptor('warn', originalConsole.warn);
    console.error = createInterceptor('error', originalConsole.error);
    console.debug = createInterceptor('debug', originalConsole.debug);
  }

  private setupErrorHandler() {
    window.addEventListener('error', (event) => {
      this.addLog('error', [
        `Uncaught error: ${event.message}`,
        `at ${event.filename}:${event.lineno}:${event.colno}`,
        event.error?.stack,
      ]);
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.addLog('error', [
        'Unhandled promise rejection:',
        event.reason,
      ]);
    });
  }

  private addLog(level: LogLevel, args: any[]) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message: args.map(arg =>
        typeof arg === 'object' ? JSON.stringify(arg, this.getCircularReplacer()) : String(arg)
      ).join(' '),
      data: args.length === 1 && typeof args[0] === 'object' ? args[0] : undefined,
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    this.logs.push(entry);

    // Keep only last N logs
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Store in sessionStorage for persistence during session
    try {
      sessionStorage.setItem('debug_logs', JSON.stringify(this.logs.slice(-50)));
    } catch (e) {
      // Ignore quota exceeded errors
    }

    // Send to remote if enabled (for critical errors only)
    if (this.remoteLogging && (level === 'error' || level === 'warn')) {
      this.sendToRemote(entry);
    }
  }

  private getCircularReplacer() {
    const seen = new WeakSet();
    return (_key: string, value: any) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '[Circular]';
        }
        seen.add(value);
      }
      return value;
    };
  }

  private async sendToRemote(entry: LogEntry) {
    try {
      await fetch(this.remoteUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry),
      });
    } catch (e) {
      // Silently fail - don't want logging to break the app
    }
  }

  // Public API
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  clearLogs() {
    this.logs = [];
    sessionStorage.removeItem('debug_logs');
  }

  downloadLogs() {
    const content = JSON.stringify(this.logs, null, 2);
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debug-logs-${new Date().toISOString()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  enableRemoteLogging(url?: string) {
    this.remoteLogging = true;
    if (url) this.remoteUrl = url;
  }

  disableRemoteLogging() {
    this.remoteLogging = false;
  }
}

// Singleton instance
export const logger = new Logger();

// Expose to window for easy access in browser console
if (typeof window !== 'undefined') {
  (window as any).debugLogger = logger;
}

// Add helper to display logs in console
(window as any).showLogs = () => {
  console.table(logger.getLogs());
};

(window as any).downloadLogs = () => {
  logger.downloadLogs();
};

(window as any).clearLogs = () => {
  logger.clearLogs();
  console.log('Logs cleared');
};

console.log('🔍 Logger initialized. Use window.showLogs() to view, window.downloadLogs() to download, window.clearLogs() to clear');
